// Read the rendered quote so exported scope and prices cannot drift from the preview.
export function createQuotePdfDefinition(quote, { kind = 'Business' } = {}) {
  const text = element => element.textContent.replace(/\s+/g, ' ').trim();
  function convert(element) {
    const tag = element.tagName;
    if (element.hasAttribute('data-quote-controls')) return [];
    if (/^H[234]$/.test(tag)) return [{ text: text(element), style: tag === 'H3' ? 'section' : 'subheading', headlineLevel: 1 }];
    if (tag === 'P') return [{ text: text(element), style: element.classList.contains('calc-eyebrow') ? 'eyebrow' : 'paragraph' }];
    if (tag === 'DL') return [{
      table: { widths: element.classList.contains('calc-totals') ? ['*', '*'] : ['*', 'auto'], body: Array.from(element.children).map(row => [
        { text: text(row.querySelector('dt')), bold: row.classList.contains('calc-total') },
        { text: text(row.querySelector('dd')), bold: true, alignment: 'right' }
      ]) },
      layout: 'lightHorizontalLines', margin: [0, 6, 0, 14]
    }];
    if (tag === 'UL') return [{ ul: Array.from(element.children).map(li => ({ stack: [{ text: text(li) }], unbreakable: true, margin: [0, 0, 0, 6] })), margin: [0, 0, 0, 10] }];
    if (tag === 'OL') return Array.from(element.children).map((li, index) => ({
      unbreakable: true,
      stack: [
        { text: `${String(index + 1).padStart(2, '0')}  ${text(li.querySelector('h4'))}`, style: 'subheading' },
        ...Array.from(li.querySelectorAll('p')).flatMap(convert)
      ], margin: [0, 4, 0, 6]
    }));
    if (tag === 'FOOTER') return [{ text: text(element), style: 'note', margin: [0, 16, 0, 0] }];
    const children = Array.from(element.children).flatMap(convert);
    return (element.classList.contains('quote-deliverable') || element.classList.contains('quote-company') || element.parentElement?.classList.contains('quote-terms')) ? [{ stack: children, unbreakable: true, margin: [0, 8, 0, 8] }] : children;
  }
  return {
    pageSize: 'LETTER', pageMargins: [46, 50, 46, 48],
    info: { title: `${kind} assessment quote - ${text(quote.querySelector('.quote-client strong'))}`, author: 'Blake Technologies' },
    defaultStyle: { font: 'Roboto', fontSize: 10, lineHeight: 1.25, color: '#23334b' },
    styles: {
      title: { fontSize: 25, bold: true, margin: [0, 8, 0, 12] },
      section: { fontSize: 16, bold: true, color: '#283d65', margin: [0, 18, 0, 10] },
      subheading: { fontSize: 12, bold: true, margin: [0, 4, 0, 7] },
      paragraph: { margin: [0, 0, 0, 10] },
      eyebrow: { fontSize: 9, color: '#6559a8', bold: true, margin: [0, 0, 0, 5] },
      note: { fontSize: 9, color: '#657087' }
    },
    content: [
      { text: 'BLAKE TECHNOLOGIES / ASSESSMENT QUOTE PREVIEW', style: 'eyebrow' },
      { text: text(quote.querySelector('h2')), style: 'title' },
      { text: text(quote.querySelector('.quote-client')), bold: true, margin: [0, 0, 0, 10] },
      { text: text(quote.querySelector('.quote-header > div > p:last-child')), style: 'paragraph' },
      { text: 'Estimated assessment investment (USD)', style: 'eyebrow', margin: [0, 10, 0, 5] },
      { text: text(quote.querySelector('.quote-investment strong')), fontSize: 28, bold: true, color: '#5144be', margin: [0, 0, 0, 8] },
      ...convert(quote.querySelector('.quote-body'))
    ],
    footer: (page, total) => ({ columns: [
      { text: 'Blake Technologies | Assessment quote preview', alignment: 'left' },
      { text: `${page} / ${total}`, alignment: 'right' }
    ], margin: [46, 16, 46, 0], fontSize: 8, color: '#657087' }),
    pageBreakBefore: (node, following) => node.headlineLevel === 1 && !following.some(next => next.text || next.table || next.image)
  };
}

export function quotePdfFilename(businessName) {
  const name = businessName.normalize('NFKC').replace(/[^\p{L}\p{N} _-]/gu, '').trim().replace(/\s+/g, '-').slice(0, 80) || 'Business';
  return `Blake-Technologies-${name}-Assessment-Quote.pdf`;
}

export async function downloadQuotePdf(definition, filename) {
  const [{ default: pdfMake }, { default: fonts }] = await Promise.all([
    import('pdfmake/build/pdfmake'), import('pdfmake/build/vfs_fonts')
  ]);
  pdfMake.addVirtualFileSystem(fonts);
  const blob = await new Promise(resolve => pdfMake.createPdf(definition).getBlob(resolve));
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  // Allow the browser to consume the URL before releasing it.
  setTimeout(() => URL.revokeObjectURL(url), 60000);
}
